import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Box } from "lucide-react";
import { useBrainImages } from "@/api/queries/brainImages";
import { useAgents } from "@/api/queries/agents";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ListTableShell } from "@/components/ResourceShell";
import { relativeTime } from "@/lib/relativeTime";
import { CreateBrainImageDialog } from "@/components/brain-image-actions/CreateBrainImageDialog";

const ROW_LIMIT = 50;

export function BrainImagesList() {
  const { data, isLoading, isError, error, refetch } = useBrainImages();
  const agentsQuery = useAgents();
  const navigate = useNavigate();
  const rows = (data ?? []).slice(0, ROW_LIMIT);
  const [createOpen, setCreateOpen] = useState(false);

  // "Genutzt von": aktive (nicht revoked) Agents mit diesem Image.
  const usedCount = new Map<string, number>();
  for (const a of agentsQuery.data ?? []) {
    if (a.brainSpec?.imageId && !a.revokedAt) usedCount.set(a.brainSpec.imageId, (usedCount.get(a.brainSpec.imageId) ?? 0) + 1);
  }
  const activeCount = (data ?? []).filter((b) => b.active).length;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <p className="text-[13px] text-text3">{data?.length ?? 0} Images · {activeCount} aktiv</p>
        <Button onClick={() => setCreateOpen(true)} data-testid="action-create-brainimage">
          <Plus className="h-4 w-4" />
          Neues Image
        </Button>
      </div>

      <ListTableShell
        isLoading={isLoading && !data}
        isError={isError}
        errorMessage={(error as Error | undefined)?.message}
        isEmpty={!isLoading && (data?.length ?? 0) === 0}
        emptyMessage="Noch keine Brain-Images"
        emptyHint="Klick 'Neues Image' zum Anlegen"
        onRetry={() => refetch()}
        rowLimit={ROW_LIMIT}
        totalRows={data?.length}
      >
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Image</TableHead>
              <TableHead>Image-Ref</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Genutzt von</TableHead>
              <TableHead className="text-right">Erstellt</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((b) => (
              <TableRow key={b.id} data-clickable="true" onClick={() => navigate(`/brain-images/${b.id}`)}>
                <TableCell>
                  <div className="flex items-center gap-3">
                    <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg border border-border bg-surface2 text-text3">
                      <Box className="h-4 w-4" />
                    </span>
                    <div className="min-w-0">
                      <div className="font-semibold text-text">{b.name}</div>
                      {b.description && <div className="truncate text-[12px] text-text3">{b.description}</div>}
                    </div>
                  </div>
                </TableCell>
                <TableCell><span className="mono text-[11.5px] text-text2">{b.imageRef}</span></TableCell>
                <TableCell>
                  <Badge variant={b.active ? "active" : "disabled"}>
                    {b.active && <span className="h-1.5 w-1.5 rounded-full bg-current" />}
                    {b.active ? "active" : "inactive"}
                  </Badge>
                </TableCell>
                <TableCell className="text-right"><span className="tnum text-[13px] font-semibold text-text">{usedCount.get(b.id) ?? 0}</span></TableCell>
                <TableCell className="text-right"><span className="text-[12.5px] text-text3">{relativeTime(b.createdAt)}</span></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </ListTableShell>
      {createOpen && <CreateBrainImageDialog open onOpenChange={setCreateOpen} />}
    </div>
  );
}
